/**
 * محتوى صفحة «من نحن» (/about) — قابل للتحرير من /admin/about-us.
 *
 * التخزين في app_settings:
 *  - `about_us_images`     → { [slot]: "https://..." }  (٦ خانات، هيرو ديسكتوب/موبايل منفصلان)
 *  - `about_us_overrides`  → { "aboutus.hero.title": { ar, en }, ... }
 *
 * القراءة عامّة (loader الصفحة)، الكتابة أدمن فقط.
 */
import { createServerFn } from "@tanstack/react-start";
import { lazyValidator } from "@/lib/server/lazy-validator";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { assertAdmin } from "@/utils/adminAuth.server";
import { invalidateAppSettingsCache } from "@/lib/server/appSettings";

const IMAGES_KEY = "about_us_images";
const TEXTS_KEY = "about_us_overrides";

export const ABOUT_IMAGE_SLOTS = [
  "hero_desktop",
  "hero_mobile",
  "story",
  "experience",
  "uae",
  "cta",
] as const;
export type AboutImageSlot = (typeof ABOUT_IMAGE_SLOTS)[number];

export type AboutImages = Partial<Record<AboutImageSlot, string>>;

export type AboutTexts = Record<string, { ar?: string; en?: string }>;

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { value: unknown; expiresAt: number }>();

async function readSetting(key: string): Promise<unknown> {
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  try {
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", key)
      .maybeSingle();
    const value = data?.value ?? null;
    cache.set(key, { value, expiresAt: now + CACHE_TTL_MS });
    return value;
  } catch {
    return cached?.value ?? null;
  }
}

function parseImages(raw: unknown): AboutImages {
  const out: AboutImages = {};
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const s of ABOUT_IMAGE_SLOTS) {
      const v = (raw as Record<string, unknown>)[s];
      if (typeof v === "string" && v.trim()) out[s] = v.trim();
    }
  }
  return out;
}

export const getAboutUsContent = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ images: AboutImages; texts: AboutTexts }> => {
    const [rawImages, rawTexts] = await Promise.all([
      readSetting(IMAGES_KEY),
      readSetting(TEXTS_KEY),
    ]);
    const { aboutTextsSchema } = await import("./aboutUsContent.schemas");
    const parsedTexts = aboutTextsSchema.safeParse(rawTexts ?? {});
    return {
      images: parseImages(rawImages),
      texts: parsedTexts.success ? parsedTexts.data : {},
    };
  },
);

export const setAboutUsImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./aboutUsContent.schemas").then((m) => m.aboutImageSchema)))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const current = parseImages(await readSetting(IMAGES_KEY));
    const next: AboutImages = { ...current };
    if (data.url.trim()) next[data.slot] = data.url.trim();
    else delete next[data.slot];
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: IMAGES_KEY, value: next as unknown as never }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    cache.delete(IMAGES_KEY);
    invalidateAppSettingsCache(IMAGES_KEY);
    return { ok: true };
  });

export const updateAboutUsTexts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./aboutUsContent.schemas").then((m) => m.aboutTextsSchema)))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const cleaned: AboutTexts = {};
    for (const [k, v] of Object.entries(data)) {
      const ar = (v.ar ?? "").trim();
      const en = (v.en ?? "").trim();
      if (ar || en) cleaned[k] = { ...(ar ? { ar } : {}), ...(en ? { en } : {}) };
    }
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: TEXTS_KEY, value: cleaned as unknown as never }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    cache.delete(TEXTS_KEY);
    invalidateAppSettingsCache(TEXTS_KEY);
    return { ok: true };
  });
